import React, { useEffect, useState } from 'react';
import { Input, Form, Collapse, CollapseProps, Skeleton } from 'antd';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { observer } from 'mobx-react';
import { t } from 'i18next';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import { maxLength, onlyDigits } from '@/utils/util-functions';

import { shouldUpdateField } from '../../EngineVersionUtils';
import { StationParametersContainer, StyledTableContainer } from './styled';
import { emptyTableData } from './CentrifugalCompressors.utils';

interface TableRow {
  key: string;
  label: string;
  input: string | number;
  suffix?: string;
  isMin?: boolean;
  name?: string;
}

interface StationParametersProps {
  data: TableRow[];
  setIsSaveDisabled: (value: boolean) => void;
  showAdvanced?: boolean;
  advancedData?: TableRow[];
  activeTab?: string;
}

const StationParametersTable = observer(
  ({ data, setIsSaveDisabled, showAdvanced, advancedData, activeTab }: StationParametersProps) => {
    const [form] = Form.useForm();
    const { engineStore } = useStores();
    const { tvalue } = useMeasurement();

    const unit = engineStore.unitMesurementSystem;

    const onDefaultStationParameterKeyPress = event => onlyDigits(event);

    const areAllFormValuesFilled = (formValues: Record<string, string>): boolean => {
      return Object.values(formValues).every(value => value !== undefined && value !== '');
    };

    const [formValues, setFormValues] = useState({});

    useEffect(() => {
      if (data && data.length > 0) {
        const newFormValues = data.reduce((acc, item) => {
          if (item.name) {
            acc[item.name] = item.input;
          }
          return acc;
        }, {});

        const hasChanges = !isEqual(newFormValues, formValues);

        if (hasChanges) {
          form.setFieldsValue(newFormValues);
          setFormValues(newFormValues);
        }
      }
    }, [data]);

    useEffect(() => {
      setIsSaveDisabled(!areAllFormValuesFilled(form.getFieldsValue()));
    }, [form.getFieldsValue(), setIsSaveDisabled]);

    const handleChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const fieldName = e.target.id;
      engineStore.stationConfiguration.setStationParameterField(key, fieldName, value);
      engineStore.validateAll(tvalue);
      const validationMessage = find(engineStore.validationMessages, { field: fieldName });
      form.setFields([
        {
          name: fieldName,
          errors: validationMessage ? [validationMessage.message] : [],
        },
      ]);
    };

    const collapseItems: CollapseProps['items'] = [
      {
        key: 'advancedItems',
        label: t('advancedSettings'),
        children: (
          <StationParametersTable activeTab={activeTab} setIsSaveDisabled={setIsSaveDisabled} data={advancedData} />
        ),
      },
    ];

    const columns = [
      {
        title: t('settings'),
        dataIndex: 'label',
        key: 'label',
        render: (text: string) => <span>{text}</span>,
      },
      {
        title: t('Parameter'),
        dataIndex: 'input',
        key: 'input',
        onCell: () => ({
          className: 'custom',
        }),
        render: (_: string, record: TableRow) => {
          const validationMessage = engineStore.validationMessages.find(
            msg => msg.field === record.name && msg.field === form.getFieldValue('activeField'),
          );

          return engineStore.stationConfiguration.isLoading ? (
            <Skeleton.Input active size="small" style={{ width: '100%' }} />
          ) : (
            <Form.Item
              key={record.key}
              name={record.name}
              validateStatus={validationMessage ? 'error' : undefined}
              help={validationMessage?.message}
              style={{ marginBottom: 0 }}
              shouldUpdate={(prevValues, currentValues) => shouldUpdateField(record.name, prevValues, currentValues)}
            >
              <Input
                placeholder="+"
                onChange={handleChange(record.key)}
                count={maxLength(4)}
                suffix={record?.suffix}
                onKeyPress={onDefaultStationParameterKeyPress}
                status={validationMessage ? 'error' : undefined}
                onFocus={e => {
                  form.setFieldValue('activeField', e.target.name);
                }}
                onBlur={() => {
                  form.setFieldValue('activeField', null);
                }}
                disabled={activeTab === 'published'}
              />
            </Form.Item>
          );
        },
      },
    ];

    return (
      <StationParametersContainer $activeTab={activeTab}>
        {showAdvanced && (
          <h4 className="advanced-title">
            {t('Measurement system')}: {unit?.charAt(0).toUpperCase() + unit?.slice(1)}
          </h4>
        )}
        <Form form={form}>
          <StyledTableContainer
            columns={columns}
            dataSource={!data || data.length === 0 ? emptyTableData : data}
            pagination={false}
            bordered={false}
          />
        </Form>
        {showAdvanced && <Collapse accordion ghost items={collapseItems} />}
      </StationParametersContainer>
    );
  },
);

export default StationParametersTable;
